import * as React from 'react';

import { enumWorkOrderStatus } from '@rematter/frontend-lib-api-client';
import { Button, EntityDetails } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  LucideCheckCircle,
  LucideFolderOpen,
  LucidePlay,
  LucideXCircle,
} from 'lucide-react';

import {
  openWorkOrderAtom,
  startWorkOrderAtom,
  cancelWorkOrderAtom,
} from '../../_graphql/mutationWorkOrder';
import * as state from '../../_state/workOrderDetail.state';
import { useTranslations } from '../../i18n/useTranslations';

import { WorkOrderCompletionDialog } from './WorkOrderCompletionDialog';
import { WorkOrderDetailTabContent } from './WorkOrderDetailTabContent';
import { WorkOrderStatusBadge } from './WorkOrderStatusBadge';

import { $location } from '~/shared/state';

//
// ⚛️ ⚛️ ⚛️ WORK ORDER DETAIL VIEW ⚛️ ⚛️ ⚛️
//
export const WorkOrderDetailView = () => {
  const t = useTranslations();

  // States
  const [isActioning, setIsActioning] = React.useState(false);
  const [isCompletionDialogOpen, setIsCompletionDialogOpen] =
    React.useState(false);

  // Atoms
  const {
    data: workOrder,
    isFetching,
    error,
    refetch,
  } = useAtomValue(state.$remoteWorkOrder);
  const setLocation = useSetAtom($location);
  const openWorkOrder = useSetAtom(openWorkOrderAtom({}));
  const startWorkOrder = useSetAtom(startWorkOrderAtom({}));
  const cancelWorkOrder = useSetAtom(cancelWorkOrderAtom({}));

  // Other data hooks
  const toast = useToast();

  const canOpen = workOrder?.status === enumWorkOrderStatus.Draft;
  const canStart = workOrder?.status === enumWorkOrderStatus.Open;
  const canComplete = workOrder?.status === enumWorkOrderStatus.InProgress;
  const canCancel =
    workOrder != null &&
    workOrder.status !== enumWorkOrderStatus.Completed &&
    workOrder.status !== enumWorkOrderStatus.Cancelled;

  // Callbacks
  const handleBack = React.useCallback(() => {
    setLocation({ pathname: '/production/work-orders' });
  }, [setLocation]);

  const handleOpen = React.useCallback(async () => {
    if (!workOrder) return;
    try {
      setIsActioning(true);
      await openWorkOrder({ id: workOrder.id });
      toast.success('Work order opened');
      refetch();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to open work order',
      );
    } finally {
      setIsActioning(false);
    }
  }, [workOrder, openWorkOrder, toast, refetch]);

  const handleStart = React.useCallback(async () => {
    if (!workOrder) return;
    try {
      setIsActioning(true);
      await startWorkOrder({ id: workOrder.id });
      toast.success('Work order started');
      refetch();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to start work order',
      );
    } finally {
      setIsActioning(false);
    }
  }, [workOrder, startWorkOrder, toast, refetch]);

  const handleComplete = React.useCallback(() => {
    if (!workOrder) return;
    setIsCompletionDialogOpen(true);
  }, [workOrder]);

  const handleCompletionSuccess = React.useCallback(() => {
    setIsCompletionDialogOpen(false);
    refetch();
  }, [refetch]);

  const handleCancel = React.useCallback(async () => {
    if (!workOrder) return;
    try {
      setIsActioning(true);
      await cancelWorkOrder({ id: workOrder.id });
      toast.success('Work order cancelled');
      refetch();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to cancel work order',
      );
    } finally {
      setIsActioning(false);
    }
  }, [workOrder, cancelWorkOrder, toast, refetch]);

  return (
    <>
      <EntityDetails
        className="@WorkOrderDetailView"
        error={
          error ??
          (!workOrder && !isFetching ? new Error('Work order not found') : null)
        }
        loading={isFetching && !workOrder}
        onBackPressed={handleBack}
        primaryTitle={
          workOrder
            ? t.workOrderDetailView.title(workOrder.workOrderNumber)
            : ''
        }
        badges={
          workOrder ? <WorkOrderStatusBadge status={workOrder.status} /> : null
        }
        headerActions={
          workOrder ? (
            <div className="flex items-center gap-2">
              {canOpen && (
                <Button
                  icon={<LucideFolderOpen />}
                  color="primary"
                  onPress={handleOpen}
                  isDisabled={isActioning}
                >
                  {t.workOrdersView.actions.open}
                </Button>
              )}
              {canStart && (
                <Button
                  icon={<LucidePlay />}
                  color="primary"
                  onPress={handleStart}
                  isDisabled={isActioning}
                >
                  {t.workOrdersView.actions.start}
                </Button>
              )}
              {canComplete && (
                <Button
                  icon={<LucideCheckCircle />}
                  color="success"
                  onPress={handleComplete}
                  isDisabled={isActioning}
                >
                  {t.workOrdersView.actions.complete}
                </Button>
              )}
              {canCancel && (
                <Button
                  icon={<LucideXCircle />}
                  variant="outline"
                  color="failure"
                  onPress={handleCancel}
                  isDisabled={isActioning}
                >
                  {t.workOrdersView.actions.cancel}
                </Button>
              )}
            </div>
          ) : null
        }
        tabs={{
          overview: {
            title: t.workOrderDetailView.tabs.overview,
            content: WorkOrderDetailTabContent,
          },
        }}
        currentTab="overview"
        onChangeTab={React.useCallback((_tab: 'overview') => undefined, [])}
      />

      {workOrder && (
        <WorkOrderCompletionDialog
          isOpen={isCompletionDialogOpen}
          onClose={() => setIsCompletionDialogOpen(false)}
          onSuccess={handleCompletionSuccess}
          workOrderId={workOrder.id}
          facilityId={workOrder.facilityId}
          inputItems={workOrder.inputItems}
          outputItems={workOrder.outputItems}
        />
      )}
    </>
  );
};
